// `messageSequence` is the backend's authoritative total order: ChatMessageWriter
// assigns it atomically (the session's incremented messageCount) on every append,
// so every persisted message — user, assistant, system marker — has a strictly
// increasing sequence that reflects the true order of the stream.
//
// Under the one-message-per-turn model this sort is mostly a safety net rather than
// load-bearing: a whole assistant turn (all its iterations and validation results)
// is a single message with one stable sequence, and its in-place part appends never
// change that sequence — so it cannot drift relative to the user message before it.
// There are no separate validation/iteration sibling rows left to interleave.
//
// We still honor sequence and nothing else, so reconnect snapshots and any legacy
// multi-row sessions stay correctly ordered. Messages that do not yet have a
// sequence are client-only and still in flight (an optimistic send the server
// hasn't echoed, or the live streaming turn before its message_added reconciles
// it). They are "pending" and stay after the persisted history in their insertion
// order — always the newest thing on screen. No timestamp/role guessing: the
// sequence is the single source of truth, and once a pending message reconciles it
// carries its real sequence and drops into place.

function hasMessageSequence(msg) {
  return typeof msg?.messageSequence === "number" && msg.messageSequence > 0;
}

export function sortSessionMessages(messages) {
  if (!Array.isArray(messages) || messages.length < 2) {
    return messages || [];
  }
  // Decorate-sort-undecorate guarantees a stable order: ties and pending
  // (sequence-less) messages keep their original insertion order.
  return messages
    .map((msg, index) => ({ msg, index }))
    .sort((a, b) => {
      const seqA = hasMessageSequence(a.msg);
      const seqB = hasMessageSequence(b.msg);
      if (seqA && seqB) {
        return a.msg.messageSequence !== b.msg.messageSequence
          ? a.msg.messageSequence - b.msg.messageSequence
          : a.index - b.index;
      }
      if (seqA) return -1;
      if (seqB) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.msg);
}
